import { useCallback } from 'react';
import { useAppEnvironmentProvider } from 'contexts/AppEnvironmentContext';
import { ELECTRON_MEET_WINDOW_EVENTS } from '@web-chat-core/toolkit/multi-window/constants';
import { saveNotificationsSettingOAuth, saveSoundSettingOAuth } from 'features/AccessRequests/api/accessRequestsApi';
import { selectEmbeddedWindowData } from 'state/selectors/app';

type PersistResult = { hasError: boolean; failedFields: string[] };

const useIpcPersist = () => {
    const { multiWindowMessagingService } = useAppEnvironmentProvider();
    const embeddedDescriptor = selectEmbeddedWindowData()?.descriptor;
    return { multiWindowMessagingService, embeddedDescriptor };
};

export const usePersistSoundSetting = () => {
    const { multiWindowMessagingService, embeddedDescriptor } = useIpcPersist();

    return useCallback(
        async (soundEnabled: boolean): Promise<PersistResult> => {
            if (!embeddedDescriptor || !multiWindowMessagingService) {
                return saveSoundSettingOAuth(soundEnabled);
            }

            return new Promise((resolve) => {
                const handler = (result: PersistResult) => {
                    window.clearTimeout(timeoutId);
                    multiWindowMessagingService.removeEventListener(handler);
                    resolve(result);
                };

                const timeoutId = window.setTimeout(() => {
                    multiWindowMessagingService.removeEventListener(handler);
                    resolve({ hasError: true, failedFields: ['sound'] });
                }, 10_000);

                multiWindowMessagingService.addEventListener(
                    ELECTRON_MEET_WINDOW_EVENTS.ACCESS_REQUESTS_SOUND_SAVE_RESULT,
                    handler,
                    embeddedDescriptor,
                );
                multiWindowMessagingService.emit(
                    ELECTRON_MEET_WINDOW_EVENTS.ACCESS_REQUESTS_SOUND_SAVE,
                    soundEnabled,
                    embeddedDescriptor,
                );
            });
        },
        [embeddedDescriptor, multiWindowMessagingService],
    );
};

export const usePersistNotificationsSetting = () => {
    const { multiWindowMessagingService, embeddedDescriptor } = useIpcPersist();

    return useCallback(
        async (notificationsEnabled: boolean): Promise<PersistResult> => {
            if (!embeddedDescriptor || !multiWindowMessagingService) {
                return saveNotificationsSettingOAuth(notificationsEnabled);
            }

            return new Promise((resolve) => {
                const handler = (result: PersistResult) => {
                    window.clearTimeout(timeoutId);
                    multiWindowMessagingService.removeEventListener(handler);
                    resolve(result);
                };

                const timeoutId = window.setTimeout(() => {
                    multiWindowMessagingService.removeEventListener(handler);
                    resolve({ hasError: true, failedFields: ['notifications'] });
                }, 10_000);

                multiWindowMessagingService.addEventListener(
                    ELECTRON_MEET_WINDOW_EVENTS.ACCESS_REQUESTS_NOTIFICATIONS_SAVE_RESULT,
                    handler,
                    embeddedDescriptor,
                );
                multiWindowMessagingService.emit(
                    ELECTRON_MEET_WINDOW_EVENTS.ACCESS_REQUESTS_NOTIFICATIONS_SAVE,
                    notificationsEnabled,
                    embeddedDescriptor,
                );
            });
        },
        [embeddedDescriptor, multiWindowMessagingService],
    );
};
